'use client';

import React, { useEffect } from 'react';
import { useCartStore } from '@/ui/store/cart';
import { useRouter } from 'next/navigation';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { paths } from '@/constants/paths';
import { ResolvedItem } from '@/ui/utils/direct-purchase-generic/resolve-direct-purchase-generic-items';
import { NormalizedDirectPurchaseItem } from '@/ui/utils/direct-purchase-generic/types/normalized-direct-purchase-item';
import { CrystallizeProductData } from '../ProductDetail/types/CrystallizeProductData';
import Spinner from '../../shared/Spinner/Spinner';
import { addResolvedItemsToCart } from './add-resolved-items-to-cart';

export type DirectPurchaseSingleProps = {
  mode?: 'single';
  data: CrystallizeProductData;
  shapeId: string;
  nukiIds: string[];
  variantSKU?: string;
  bundleProductSKUs?: string[];
  bundleExtRef?: string;
};

export type DirectPurchaseMultiProps = {
  mode: 'multi';
  resolvedItems: ResolvedItem[];
};

export type DirectPurchaseProps = DirectPurchaseSingleProps | DirectPurchaseMultiProps;

const buildNormalizedItems = (
  props: DirectPurchaseProps,
): NormalizedDirectPurchaseItem[] => {
  if (props.mode === 'multi') {
    return props.resolvedItems.map((it) => {
      if (it.kind === 'sku') {
        return {
          kind: 'product',
          shapeId: it.shapeId,
          contentData: it.contentData,
          variantSKU: it.variantSKU,
          quantity: it.quantity,
        };
      }
      return {
        kind: 'standaloneBundle',
        bundleData: it.bundleData,
        priceVariantId: it.priceVariantId,
        quantity: it.quantity,
      };
    });
  }
  return [
    {
      kind: 'product',
      shapeId: props.shapeId,
      contentData: props.data,
      variantSKU: props.variantSKU,
      bundleProductSKUs: props.bundleProductSKUs,
      bundleExtRef: props.bundleExtRef,
      nukiIds: props.nukiIds,
      quantity: 1,
    },
  ];
};

export function DirectPurchaseView(props: Readonly<DirectPurchaseProps>) {
  const cartStore = useCartStore();
  const { localizedLink } = useLocaleLink();
  const router = useRouter();

  const parseAndAddToCart = () => {
    const items = buildNormalizedItems(props);
    let isSingleEmpty = false;
    // eslint-disable-next-line react/destructuring-assignment
    if (props.mode !== 'multi') {
      const { shapeId, data } = props;
      isSingleEmpty = !shapeId || !data;
    }

    if (isSingleEmpty) {
      router.push(localizedLink(paths.home));
      return;
    }

    const ok = addResolvedItemsToCart(items, cartStore);
    if (!ok) {
      router.push(localizedLink(paths.home));
      return;
    }
    router.push(localizedLink(paths.checkout));
  };

  useEffect(() => {
    parseAndAddToCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner className="h-10 w-10" />
    </div>
  );
}
